import React, { FC, MouseEvent, useCallback, useRef } from "react";
import CopyToClipboard from "components/CopyToClipboard/CopyToClipboard";
import { IconPng } from "components/Icon/Icon";
import { IconType } from "enums/Icon";
import { List } from "immutable";
import { isMobile } from "react-device-detect";
import { Hidden, Visible } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import Checkbox from "../../../../components/Checkbox/Checkbox";
import PackageTypeIcon from "../../../../components/PackageTypeIcon/PackageTypeIcon";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { panelSelectOpened } from "../../../../reduxFolder/selectors/rightPanelSelectors";
import { DefaultParcelIcon, ParcelLogo } from "../../../../styles/parcelsStyles";
import { isTransferringState } from "../../../../utils/common";
import {
  extractConsolidationId,
  formatConsolidationSku,
} from "../../../../utils/consolidationSku";
import { useFormattedDate } from "../../../../utils/formatDate";
import { getAddonsIconsImage } from "../../../Delivery/deliveryHelper";
import { useDemoHint } from "../DemoShipments/useDemoHint";
import { isUnprocessedRequest } from "../ServiceRequests/requestHelper";
import ServiceTooltips from "../ServiceTooltip/ServiceTooltips";
import {
  CheckboxAreaButton,
  Comment,
  DemoBadge,
  Description,
  EmptyConsolidationSpacing,
  IconGroup,
  MainStorageCol,
  MobileCheckboxAreaButton,
  MobileCheckboxAreaButton2,
  NoCheckboxActionArea,
  ParcelDate,
  WeightText,
  RedirectLabel,
  RedirectLinksGroup,
  RedirectTokenLink,
  RightArrowIcon,
  StorageCol,
  StorageDescriptionCol,
  StorageItemRowWrapper,
  StorageMetaCol,
  StorageRow,
  TrackingNumber,
  TrackingNumberEnd,
  TrackingNumberStart,
  TransferIcon,
  WarningText,
} from "./StorageList.styles";

// How the row adapts to the width available to the list: drop the date
// column first, then the weight column (measured by StorageList).
export type RowLayoutTier = "full" | "noDate" | "trackingOnly";

// Keep this many trailing characters visible when the tracking number is too
// long for the column and gets middle-truncated ("92612999…635420").
const TRACKING_TAIL_CHARS = 6;

// Fixed desktop cell widths, sized to the longest localized content so the
// cells stay aligned across rows and never wrap.
const WEIGHT_COL_WIDTH = 110;
const DATE_COL_WIDTH = 160;

// List widths (px) below which trailing cells are dropped. Derived from the
// cell widths above plus the checkbox/logo area (~105px) and an untruncated
// tracking number (~250px): below these, a cell would leave almost no gap.
const DATE_COLUMN_MIN_WIDTH = 660;
const WEIGHT_COLUMN_MIN_WIDTH = 500;

export const getLayoutTier = (listWidth: number): RowLayoutTier => {
  if (listWidth < WEIGHT_COLUMN_MIN_WIDTH) return "trackingOnly";
  if (listWidth < DATE_COLUMN_MIN_WIDTH) return "noDate";
  return "full";
};

const renderTrackingNumber = (description: string | null | undefined) => {
  if (!description || description.length <= TRACKING_TAIL_CHARS * 2) {
    return description;
  }

  return (
    <TrackingNumber>
      <TrackingNumberStart>
        {description.slice(0, -TRACKING_TAIL_CHARS)}
      </TrackingNumberStart>
      <TrackingNumberEnd>
        {description.slice(-TRACKING_TAIL_CHARS)}
      </TrackingNumberEnd>
    </TrackingNumber>
  );
};

type ConsolidationLink = {
  id: number;
  label: string;
};

const getConsolidationLinks = (parcel: any): ConsolidationLink[] => {
  const shipmentItems = parcel.get("items", List());
  const itemsQuantity = Number(
    parcel.get("items_quantity", shipmentItems.size),
  );
  const isMultiItemShipment = itemsQuantity > 1 || shipmentItems.size > 1;

  if (isMultiItemShipment) {
    if (!shipmentItems.size) {
      return [];
    }

    const inventoryItems = shipmentItems.filter((item: any) => {
      const sku = item.get("sku");

      return typeof sku === "string" && sku.startsWith("I");
    });

    if (!inventoryItems.size) {
      return [];
    }

    const seenIds = new Set<number>();
    const consolidationLinks: ConsolidationLink[] = [];

    for (const item of inventoryItems.toArray()) {
      const consolidationSku = item.getIn(["consolidation", "sku"]);
      const consolidationId =
        extractConsolidationId(item.getIn(["consolidation", "id"])) ??
        extractConsolidationId(consolidationSku);

      if (consolidationId === null) {
        return [];
      }

      if (!seenIds.has(consolidationId)) {
        seenIds.add(consolidationId);
        consolidationLinks.push({
          id: consolidationId,
          label: consolidationSku || formatConsolidationSku(consolidationId),
        });
      }
    }

    return consolidationLinks;
  }

  const consolidationId = extractConsolidationId(
    parcel.get("consolidation_id"),
  );

  return consolidationId === null
    ? []
    : [{ id: consolidationId, label: formatConsolidationSku(consolidationId) }];
};

interface StorageItemRowProps {
  parcel: any;
  selectedParcel: number | null;
  onFocusStorageItemInPanel: (item: any) => void;
  isChecked: boolean;
  disabled: boolean;
  isPanelOpened: boolean;
  units: string;
  addons: any;
  layoutTier: RowLayoutTier;
  handleSelect: (id: string | number) => void;
  selectedRangeParcels: (id: string | number) => any;
  isDemo?: boolean;
}

const StorageItemRow: FC<StorageItemRowProps> = React.memo(
  ({
    units,
    parcel,
    isChecked,
    handleSelect,
    selectedRangeParcels,
    isPanelOpened,
    selectedParcel,
    onFocusStorageItemInPanel,
    addons,
    layoutTier,
    disabled,
    isDemo,
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation("common");
    const showDemoHint = useDemoHint();

    const isClientTransferring = isTransferringState(parcel.get("state"));
    const logoUrl = parcel.get("emblem_thumb_url");
    const parcelDate = useFormattedDate({
      date: parcel.get("received_at", new Date()),
      hideCurrentYearDisplay: true,
    });

    const requests = parcel.get("serviceRequestCount");
    const isProhibited = parcel.get("prohibited") || false;
    const isDisabled =
      !!isUnprocessedRequest(requests) ||
      isProhibited === true ||
      isClientTransferring;

    const tooltipBody =
      isDisabled &&
      (isProhibited
        ? t("parcels.shipmentProhibited")
        : t("parcels.shipmentNotSend"));
    const isSelectedShipment =
      (isMobile || isPanelOpened) && selectedParcel === parcel.get("id");
    const comment = parcel.get("customer_comment");
    const weight =
      parcel.get("weight") && Number(parcel.get("weight")).toFixed(2);
    const consolidationLinks = getConsolidationLinks(parcel);
    const hasConsolidation = consolidationLinks.length > 0;
    const package_type = parcel.get("package_type");
    const parcelAddons = parcel.get("addons");

    const handleItemSelect = useCallback(
      () => handleSelect(parcel.get("id")),
      [handleSelect, parcel],
    );
    const handleCheckBoxClick = useCallback(
      (event: MouseEvent<HTMLLabelElement | HTMLButtonElement>) => {
        event.stopPropagation();

        if (isDemo) {
          showDemoHint();
          return;
        }
        if (isDisabled || hasConsolidation) return;

        if (event.nativeEvent.shiftKey) {
          const selectedParcels = selectedRangeParcels(parcel.get("id"));

          handleSelect(selectedParcels);
        } else {
          handleItemSelect();
        }
      },
      [
        selectedRangeParcels,
        parcel,
        handleSelect,
        handleItemSelect,
        hasConsolidation,
        isDisabled,
        isDemo,
        showDemoHint,
      ],
    );

    const handleParcelLogoClick = useCallback(
      (event: MouseEvent) => {
        event.stopPropagation();

        if (isDemo) {
          showDemoHint();
          return;
        }
        if (isDisabled || hasConsolidation) return;

        handleItemSelect();
      },
      [handleItemSelect, hasConsolidation, isDisabled, isDemo, showDemoHint],
    );

    const compactDescription = (description: string) => {
      if (description && description.length > 20) {
        let chars = description.split("");
        chars.splice(8, 8, "...");
        return chars.join("");
      } else {
        return description;
      }
    };

    const prohibitedIcon = (isProhibited: boolean) => {
      return isProhibited ? (
        <Tooltip
          body={tooltipBody}
          place="right"
          openEvents={{
            click: isMobile,
            mouseover: !isMobile,
            focus: !isMobile,
          }}
          className="tooltip-content-checkbox"
        >
          <WarningText>!</WarningText>
        </Tooltip>
      ) : null;
    };

    const showWeightCol = layoutTier !== "trackingOnly";
    const showDateCol = layoutTier === "full";

    const redirectPath = `/parcels/${parcel.get("state") === "shipped" ? "history" : "outgoing"}`;

    const getRedirectState = (consolidationId: number) => ({
      consolidationId,
      shippingMethod: parcel.get("carrier"),
      weekNumber: parcel.get("week_number"),
      shipmentId: parcel.get("id"),
      receivedAt: parcel.get("shipped_on"),
    });

    const renderRedirectLinks = () => {
      return (
        <RedirectLinksGroup>
          <RedirectLabel>{t("common.sentIn")}</RedirectLabel>
          {consolidationLinks.map((consolidation) => (
            <RedirectTokenLink
              key={consolidation.id}
              to={redirectPath}
              state={getRedirectState(consolidation.id)}
              onClick={(event) => event.stopPropagation()}
            >
              <span>{consolidation.label}</span>
              <RightArrowIcon type={IconType.RightArrow} />
            </RedirectTokenLink>
          ))}
        </RedirectLinksGroup>
      );
    };

    return (
      <StorageItemRowWrapper ref={itemRef} disabled={disabled && isChecked}>
        <StorageRow
          align={"center"}
          checked={isChecked}
          data-parcel-id={parcel.get("id")}
          selected={isSelectedShipment}
          styled={{ hasConsolidation }}
          onClick={() => onFocusStorageItemInPanel(parcel)}
        >
          {/* Mobile version of rows */}
          <StorageCol xs={2}>
            <Visible xs>
              {isChecked ? (
                <MobileCheckboxAreaButton2
                  onClick={(e: MouseEvent<HTMLButtonElement>) =>
                    handleCheckBoxClick(e)
                  }
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={handleItemSelect}
                    onClick={(e: MouseEvent<HTMLLabelElement>) =>
                      handleCheckBoxClick(e)
                    }
                    disabled={isDisabled || !!isDemo}
                  />
                </MobileCheckboxAreaButton2>
              ) : (
                <MobileCheckboxAreaButton onClick={handleParcelLogoClick}>
                  {logoUrl ? (
                    <ParcelLogo
                      image={logoUrl}
                      onClick={
                        isDisabled || hasConsolidation
                          ? null
                          : handleParcelLogoClick
                      }
                      disabled={isDisabled}
                    />
                  ) : (
                    <DefaultParcelIcon />
                  )}
                  {isClientTransferring && (
                    <TransferIcon type="transfer" width="14" height="14" />
                  )}
                </MobileCheckboxAreaButton>
              )}
            </Visible>
          </StorageCol>
          <StorageCol xs={10}>
            <Visible xs>
              <StorageCol xs={hasConsolidation ? 3 : 5}>
                <Description disabled={!!isDisabled}>
                  {prohibitedIcon(isProhibited)}
                  <CopyToClipboard text={parcel.get("description")}>
                    {compactDescription(parcel.get("description"))}
                  </CopyToClipboard>
                  {isDemo && <DemoBadge>{t("parcels.demo.badge")}</DemoBadge>}
                  {comment && <Comment>{comment}</Comment>}
                </Description>
              </StorageCol>
              <StorageCol xs={hasConsolidation ? 2 : 3}>
                <IconGroup>
                  {getAddonsIconsImage(parcelAddons, addons, 12)}
                  {!!requests.size && (
                    <ServiceTooltips
                      requests={requests.toJS()}
                      fullWidth={false}
                    />
                  )}
                </IconGroup>
              </StorageCol>
              <StorageCol xs={4}>
                <PackageTypeIcon type={package_type} />
                <WeightText>{`${weight} ${t(`units.${units}.primaryShort`)}`}</WeightText>
              </StorageCol>
              {hasConsolidation && (
                <StorageCol xs={3}>{renderRedirectLinks()}</StorageCol>
              )}
            </Visible>
          </StorageCol>

          {/* Desktop version of rows */}
          <MainStorageCol sm={12}>
            <Hidden xs>
              <StorageDescriptionCol>
                {!hasConsolidation && isProhibited && (
                  <NoCheckboxActionArea>
                    {prohibitedIcon(isProhibited)}
                    {logoUrl ? <ParcelLogo image={logoUrl} /> : <DefaultParcelIcon />}
                  </NoCheckboxActionArea>
                )}
                {!hasConsolidation && !isProhibited && (
                  <CheckboxAreaButton onClick={handleCheckBoxClick}>
                    {isClientTransferring ? (
                      <IconPng type="transfer" />
                    ) : (
                      <Checkbox
                        checked={isChecked}
                        onChange={handleItemSelect}
                        disabled={isDisabled || !!isDemo}
                      />
                    )}
                    {logoUrl ? <ParcelLogo image={logoUrl} /> : <DefaultParcelIcon />}
                  </CheckboxAreaButton>
                )}
                {hasConsolidation && (
                  <NoCheckboxActionArea>
                    <EmptyConsolidationSpacing />
                    {logoUrl ? <ParcelLogo image={logoUrl} /> : <DefaultParcelIcon />}
                  </NoCheckboxActionArea>
                )}
                <Description>
                  <CopyToClipboard text={parcel.get("description")}>
                    {renderTrackingNumber(parcel.get("description"))}
                  </CopyToClipboard>
                  {isDemo && <DemoBadge>{t("parcels.demo.badge")}</DemoBadge>}
                  {getAddonsIconsImage(parcelAddons, addons)}
                  {!!requests.size && (
                    <ServiceTooltips
                      requests={requests.toJS()}
                      fullWidth={false}
                    />
                  )}
                  {comment && <Comment>{comment}</Comment>}
                </Description>
              </StorageDescriptionCol>
              {showWeightCol && (
                <StorageMetaCol $width={WEIGHT_COL_WIDTH}>
                  <PackageTypeIcon type={package_type} />
                  <WeightText>{`${weight} ${t(`units.${units}.primaryShort`)}`}</WeightText>
                </StorageMetaCol>
              )}
              {showDateCol && (
                <StorageMetaCol $width={DATE_COL_WIDTH}>
                  <ParcelDate>{parcelDate}</ParcelDate>
                </StorageMetaCol>
              )}
              {hasConsolidation && (
                <StorageMetaCol>{renderRedirectLinks()}</StorageMetaCol>
              )}
            </Hidden>
          </MainStorageCol>
        </StorageRow>
      </StorageItemRowWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    isPanelOpened: panelSelectOpened,
  }),
);

export default withConnect(StorageItemRow);
